import * as Castle from "@castleio/castle-js";
import { SignupBudgetSpentData } from "@pax/api-contract/accounting/v1alpha1";
import { ClientAllowanceSpentData } from "@pax/api-contract/ai/shared";
import type { ClientConfig } from "@pax/api-contract/client-config";
import {
  AccountBannedData,
  StepUpRequiredData,
  type VerifyPersonMethod,
} from "@pax/api-contract/common";
import {
  CARD_VERIFICATION_RETURN_PARAM,
  type CardVerificationOutcome,
  SIGN_IN_CHECK_RECAPTCHA_ACTION,
} from "@pax/api-contract/risk/v1alpha1";
import { useSyncExternalStore } from "react";
import { type ApiRefusal, api, backgroundApi, fetchClientConfig } from "./api";

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready(callback: () => void): void;
        execute(siteKey: string, options: { action: string }): Promise<string>;
      };
    };
  }
}

type CastleClient = ReturnType<typeof Castle.configure>;

let config: Promise<ClientConfig> | undefined;
let castle: Promise<CastleClient | null> | undefined;
let recaptcha: Promise<NonNullable<Window["grecaptcha"]>["enterprise"]> | undefined;

function clientConfig(): Promise<ClientConfig> {
  config ??= fetchClientConfig().catch(error => {
    config = undefined;
    throw error;
  });
  return config;
}

/** Castle's SDK, configured once per tab with the deployment's publishable key, if it has one. */
function castleClient(): Promise<CastleClient | null> {
  castle ??= clientConfig()
    .then(({ castlePublishableKey: pk }) => (pk ? Castle.configure({ pk }) : null))
    .catch(error => {
      castle = undefined;
      throw error;
    });
  return castle;
}

/** A fresh Castle request token, or undefined when the deployment runs without Castle. */
async function castleToken(): Promise<string | undefined> {
  return (await castleClient())?.createRequestToken();
}

async function requiredCastleToken(): Promise<string> {
  const token = await castleToken();
  if (!token) throw new Error("This deployment has no Castle key.");
  return token;
}

/** Loads reCAPTCHA Enterprise with the web key on first use. */
function recaptchaClient(siteKey: string) {
  recaptcha ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onerror = () => {
      recaptcha = undefined;
      reject(new Error("reCAPTCHA failed to load."));
    };
    script.onload = () => {
      const enterprise = window.grecaptcha?.enterprise;
      if (!enterprise) {
        recaptcha = undefined;
        return reject(new Error("reCAPTCHA failed to load."));
      }
      enterprise.ready(() => resolve(enterprise));
    };
    document.head.append(script);
  });
  return recaptcha;
}

/**
 * A token from the score-only web key, which never shows a puzzle, for the `sign_in_check` action.
 * Undefined when the deployment has no key.
 */
async function recaptchaToken(): Promise<string | undefined> {
  const { recaptchaWebSiteKey } = await clientConfig();
  if (!recaptchaWebSiteKey) return undefined;
  const enterprise = await recaptchaClient(recaptchaWebSiteKey);
  return enterprise.execute(recaptchaWebSiteKey, { action: SIGN_IN_CHECK_RECAPTCHA_ACTION });
}

/** How long a sign-in check waits for reCAPTCHA before checking with Castle alone. */
const SIGN_IN_RECAPTCHA_TIMEOUT_MS = 5_000;

let checkInFlight: Promise<void> | undefined;

/**
 * Checks this pending session: mints a Castle request token, which fingerprints the device, runs an
 * invisible reCAPTCHA, and posts both. A reCAPTCHA that fails or is slow is left out. Callers at
 * the same moment share one check, which rejects while Castle can't answer, so they retry.
 */
export function checkSession(): Promise<void> {
  checkInFlight ??= (async () => {
    const [castleToken, recaptcha] = await Promise.all([
      requiredCastleToken(),
      Promise.race([
        recaptchaToken().catch(() => undefined),
        new Promise<undefined>(resolve => setTimeout(resolve, SIGN_IN_RECAPTCHA_TIMEOUT_MS)),
      ]),
    ]);
    await api.risk.signInChecks.create({
      body: { castleToken, ...(recaptcha ? { recaptchaToken: recaptcha } : {}) },
    });
  })().finally(() => {
    checkInFlight = undefined;
  });
  return checkInFlight;
}

/** Whether this deployment checks new sessions with Castle, so they start pending. */
export async function checksSessions(): Promise<boolean> {
  return Boolean((await clientConfig()).castlePublishableKey);
}

let signInCheckRetry: Promise<boolean> | undefined;

/** Requests refused at the same moment share one sign-in check. */
function retrySignInCheck(): Promise<boolean> {
  signInCheckRetry ??= checkSession()
    .then(() => true)
    .catch(() => false)
    .finally(() => {
      signInCheckRetry = undefined;
    });
  return signInCheckRetry;
}

/**
 * Finishes a card verification Stripe just sent the person back from, then claims free credits.
 * Runs after every sign-in and when the app starts; the claim is what grants signup credits and the
 * daily refresh. It never calls Castle: the session was checked when it was made.
 */
export async function checkInSession({ accountId }: { accountId: string }): Promise<boolean> {
  await completeReturnedCardVerification(accountId);
  const claim = await backgroundApi.accounting.credits.claim();
  return claim.signupGrantedRaw !== "0" || claim.refresh !== null;
}

/** Remembers what a card was added for, so the page Stripe returns to can say it. */
const CARD_ACTION_KEY = "pax.risk.card-verification-action";

/** Sends the person to Stripe to add a card, coming back to the page they're on. */
export async function startCardVerification(action: string): Promise<void> {
  try {
    sessionStorage.setItem(CARD_ACTION_KEY, action);
  } catch {
    // Without storage the notice after a refused card names the claim.
  }
  const { pathname, search } = window.location;
  const { url } = await api.risk.cardVerifications.create({
    body: { returnPath: `${pathname}${search}` },
  });
  window.location.assign(url);
}

/**
 * Completes the card verification in the address Stripe returned to, and says so when the card
 * didn't count. The parameter is removed either way.
 */
async function completeReturnedCardVerification(accountId: string): Promise<void> {
  const url = new URL(window.location.href);
  const cardVerificationId = url.searchParams.get(CARD_VERIFICATION_RETURN_PARAM);
  if (!cardVerificationId) return;
  url.searchParams.delete(CARD_VERIFICATION_RETURN_PARAM);
  window.history.replaceState(window.history.state, "", url);
  const { status } = await backgroundApi.risk.cardVerifications
    .complete({ params: { cardVerificationId } })
    .catch(() => ({ status: "not_finished" as const }));
  if (status !== "verified") {
    show(
      { kind: "verify_person", action: rememberedCardAction(), methods: ["card"], outcome: status },
      {
        accountId,
        requestId: null,
        at: new Date().toISOString(),
        code: status,
        step: "verify_person",
      },
    );
  }
}

function rememberedCardAction(): string {
  try {
    const action = sessionStorage.getItem(CARD_ACTION_KEY);
    if (action) return action;
  } catch {
    // As above.
  }
  return "Claim free credits";
}

/** A refusal the person is told about, in the words the dialog uses. */
export type RiskNotice =
  | {
      kind: "verify_person";
      /** What couldn't be completed: the refused route's summary, such as "Claim free credits". */
      action: string;
      /** The methods the account can still use; phone and ID always show as coming soon. */
      methods: VerifyPersonMethod[];
      /** How the last card verification ended, when it didn't verify the account. */
      outcome?: Exclude<CardVerificationOutcome["status"], "verified">;
    }
  | { kind: "blocked" }
  | { kind: "rate_limited"; retryAt: string | null }
  | { kind: "free_credits_paused"; resetsAt: string }
  | { kind: "client_allowance_spent"; resetsAt: string }
  | { kind: "acting_as_player" };

/** What support needs to find the refusal in the API's logs. */
export interface SupportDetails {
  accountId: string | null;
  requestId: string | null;
  at: string;
  code: string;
  step?: string;
  tier?: string;
  banCategory?: string;
}

export interface RiskState {
  notice: RiskNotice;
  details: SupportDetails;
}

/** The session facts the store reads; the auth queries own the session. */
interface SessionFacts {
  accountId: string | null;
  actingAsPlayer: boolean;
}

/** Remembers the free credit budget window already explained, so it's explained once. */
const FREE_CREDITS_PAUSED_KEY = "pax.risk.free-credits-paused-until";

let state: RiskState | null = null;
const listeners = new Set<() => void>();

function setState(next: RiskState | null): void {
  state = next;
  for (const listener of listeners) listener();
}

/** One refusal is shown at a time; a blocked account replaces whatever was showing. */
function show(notice: RiskNotice, details: SupportDetails): void {
  if (state && state.notice.kind === "blocked") return;
  if (state && notice.kind !== "blocked") return;
  setState({ notice, details });
}

export function dismissRiskNotice(): void {
  if (state?.notice.kind === "blocked") return;
  setState(null);
}

/** Clears a blocked notice once its account is signed out. */
export function clearRiskNotice(): void {
  setState(null);
}

export function useRiskState(): RiskState | null {
  return useSyncExternalStore(
    listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

/**
 * Decides what each refusal does. A sign-in check runs silently and retries, proving a person is
 * offered, and a ban shows the blocked notice. Every other refusal is explained while the request
 * fails with its original error. A background request shows only a step the person can take, a
 * ban, and the one-time note that free credits are paused.
 */
export function createRefusalHandler(readSession: () => SessionFacts) {
  return async (
    refusal: ApiRefusal,
    attempt: number,
    { background }: { background: boolean },
  ): Promise<boolean> => {
    const details: SupportDetails = {
      accountId: readSession().accountId,
      requestId: refusal.requestId,
      at: new Date().toISOString(),
      code: refusal.code,
    };
    switch (refusal.code) {
      case "STEP_UP_REQUIRED": {
        const data = StepUpRequiredData.safeParse(refusal.data).data;
        if (!data) return false;
        Object.assign(details, { step: data.step, tier: data.tier });
        if (data.step === "sign_in_check") return attempt === 0 && retrySignInCheck();
        show({ kind: "verify_person", action: data.action, methods: data.methods ?? [] }, details);
        return false;
      }
      case "ACCOUNT_BANNED": {
        details.banCategory = AccountBannedData.safeParse(refusal.data).data?.category;
        show({ kind: "blocked" }, details);
        return false;
      }
      case "SIGNUP_BUDGET_SPENT": {
        const resetsAt = SignupBudgetSpentData.safeParse(refusal.data).data?.retryAt;
        if (!resetsAt || localStorage.getItem(FREE_CREDITS_PAUSED_KEY) === resetsAt) return false;
        localStorage.setItem(FREE_CREDITS_PAUSED_KEY, resetsAt);
        show({ kind: "free_credits_paused", resetsAt }, details);
        return false;
      }
    }
    if (background) return false;
    switch (refusal.code) {
      case "FORBIDDEN":
        if (readSession().actingAsPlayer) show({ kind: "acting_as_player" }, details);
        return false;
      case "TOO_MANY_REQUESTS": {
        const retryAt =
          refusal.retryAfterSeconds === null
            ? null
            : new Date(Date.now() + refusal.retryAfterSeconds * 1000).toISOString();
        show({ kind: "rate_limited", retryAt }, details);
        return false;
      }
      case "CLIENT_ALLOWANCE_SPENT": {
        const resetsAt = ClientAllowanceSpentData.safeParse(refusal.data).data?.resetsAt;
        if (resetsAt) show({ kind: "client_allowance_spent", resetsAt }, details);
        return false;
      }
      default:
        return false;
    }
  };
}
